/* Collection filtering.

   Filtering is NOT done client-side. Every filter value already carries a URL
   from Liquid; clicking one fetches that URL with &section_id, swaps the
   results, and pushes the new address.

   pushState, not replaceState. A filter change should be back-navigable — the
   prototype uses replaceState, so the browser Back button jumps you off the
   collection entirely instead of undoing the last filter. That is a genuine
   improvement rather than a port. */
(function () {
  'use strict';

  const H = window.Hystride;
  if (!H) return;

  H.register('collection', {
    mount(root) {
      const sectionId = root.closest('.shopify-section')?.id.replace('shopify-section-', '');
      if (!sectionId) return null;

      let aborter = null;

      const render = async (url, { push = true } = {}) => {
        aborter?.abort();
        aborter = new AbortController();

        root.setAttribute('aria-busy', 'true');

        const fetchUrl = new URL(url, window.location.origin);
        fetchUrl.searchParams.set('section_id', sectionId);

        try {
          const res = await fetch(fetchUrl.toString(), { signal: aborter.signal });
          const text = await res.text();
          const doc = new DOMParser().parseFromString(text, 'text/html');

          const fresh = doc.querySelector('[data-collection-results]');
          const current = root.querySelector('[data-collection-results]');
          if (fresh && current) current.replaceWith(fresh);

          const freshCount = doc.querySelector('[data-collection-count]');
          const currentCount = root.querySelector('[data-collection-count]');
          if (freshCount && currentCount) currentCount.replaceWith(freshCount);

          if (push) {
            const clean = new URL(url, window.location.origin);
            clean.searchParams.delete('section_id');
            window.history.pushState({ hystrideFacets: true }, '', clean.toString());
          }

          H.reveal(root);
          // Anything newly in the DOM needs the hover images wired again.
          document.dispatchEvent(new CustomEvent('hystride:collection-updated', { detail: { root } }));
        } catch (err) {
          if (err.name === 'AbortError') return;
          // A failed fetch should not leave the visitor stuck on stale results
          // with no way forward — fall back to a normal navigation.
          window.location.href = url;
        } finally {
          root.removeAttribute('aria-busy');
        }
      };

      const onClick = (e) => {
        const link = e.target.closest('[data-facet-link]');
        if (link) {
          e.preventDefault();
          render(link.href);
          return;
        }

        const value = e.target.closest('[data-facet-value]');
        if (value) {
          e.preventDefault();
          render(value.dataset.url);
          return;
        }

        const toggle = e.target.closest('[data-facet-toggle]');
        if (toggle) {
          e.preventDefault();
          const group = toggle.closest('[data-facet-group]');
          const open = group.dataset.open === '1';
          closeAll();
          group.dataset.open = open ? '0' : '1';
          toggle.setAttribute('aria-expanded', String(!open));
          return;
        }

        const clear = e.target.closest('[data-facet-clear]');
        if (clear) {
          e.preventDefault();
          render(clear.href);
        }

        const mobileToggle = e.target.closest('[data-facet-mobile-toggle]');
        if (mobileToggle) {
          const groups = document.getElementById(mobileToggle.getAttribute('aria-controls'));
          const open = groups?.classList.toggle('open');
          mobileToggle.setAttribute('aria-expanded', String(!!open));
        }
      };

      const onChange = (e) => {
        const sort = e.target.closest('[data-facet-sort]');
        if (sort) {
          const url = new URL(window.location.href);
          // Omit the parameter when it is the default, so a shared link stays
          // readable rather than carrying noise.
          if (sort.value === 'manual') {
            url.searchParams.delete('sort_by');
          } else {
            url.searchParams.set('sort_by', sort.value);
          }
          render(url.toString());
          return;
        }

        const input = e.target.closest('[data-facet-input]');
        if (input) {
          const url = new URL(window.location.href);
          if (input.value) {
            url.searchParams.set(input.name, input.value);
          } else {
            url.searchParams.delete(input.name);
          }
          render(url.toString());
        }
      };

      const closeAll = () => {
        root.querySelectorAll('[data-facet-group]').forEach((g) => {
          g.dataset.open = '0';
          g.querySelector('[data-facet-toggle]')?.setAttribute('aria-expanded', 'false');
        });
      };

      // Close a dropdown when the click lands outside it, and on Escape.
      const onDocClick = (e) => {
        if (!e.target.closest('[data-facet-group]')) closeAll();
      };
      const onKey = (e) => {
        if (e.key !== 'Escape') return;
        closeAll();
      };

      const onPop = () => render(window.location.href, { push: false });

      root.addEventListener('click', onClick);
      root.addEventListener('change', onChange);
      document.addEventListener('click', onDocClick);
      document.addEventListener('keydown', onKey);
      window.addEventListener('popstate', onPop);

      return { onClick, onChange, onDocClick, onKey, onPop };
    },

    unmount(root, s) {
      if (!s) return;
      root.removeEventListener('click', s.onClick);
      root.removeEventListener('change', s.onChange);
      document.removeEventListener('click', s.onDocClick);
      document.removeEventListener('keydown', s.onKey);
      window.removeEventListener('popstate', s.onPop);
    },
  });
})();
